import React, { useMemo, useRef, useState, useEffect } from "react"
import clsx from "clsx"
import { FormikProps } from "formik"

// --- TYPES ---
export interface NestedPermission {
     value: string
     label: string
     children?: NestedPermission[]
}

interface PermissionNode extends NestedPermission {
     children: PermissionNode[]
     allDescendantValues: string[] // Pre-calculated IDs for fast selection
}

interface Props {
     name: string
     label?: string
     options: NestedPermission[] // Expects the hierarchical structure
     formikProps: FormikProps<any>
     classNameLayout?: string
}

const normalizeString = (str: string) =>
     str
          .normalize("NFD")
          .replace(/[\u0300-\u036f]/g, "")
          .toLowerCase()

const PermissionsSelectField = ({ name, label, options, formikProps, classNameLayout }: Props) => {
     const [isOpen, setIsOpen] = useState(false)
     const [searchTerm, setSearchTerm] = useState("")
     const [expandedIds, setExpandedIds] = useState<string[]>([])

     const dropdownRef = useRef<HTMLDivElement>(null)
     const selectedValues: string[] = formikProps.values[name] || []
     const isInvalid = formikProps.touched[name] && formikProps.errors[name]

     // --- 1. ENRICH TREE ---
     // Pre-calculates descendant IDs for every node to make toggling instantaneous
     const enhancedTree = useMemo(() => {
          const enrich = (node: NestedPermission): PermissionNode => {
               const children = (node.children || []).map(enrich)
               const allDescendantValues = [node.value, ...children.flatMap(c => c.allDescendantValues)]
               return { ...node, children, allDescendantValues }
          }
          return options.map(enrich)
     }, [options])

     // --- 2. SEARCH FILTERING ---
     const filteredTree = useMemo(() => {
          if (!searchTerm) return enhancedTree

          const filterNode = (node: PermissionNode): PermissionNode | null => {
               const matches = normalizeString(node.label).includes(normalizeString(searchTerm))
               const filteredChildren = node.children.map(filterNode).filter(Boolean) as PermissionNode[]

               if (matches || filteredChildren.length > 0) {
                    return { ...node, children: filteredChildren }
               }
               return null
          }
          return enhancedTree.map(filterNode).filter(Boolean) as PermissionNode[]
     }, [enhancedTree, searchTerm])

     // --- 3. SELECTION HELPERS ---
     const isNodeFullySelected = (node: PermissionNode): boolean => node.allDescendantValues.every(v => selectedValues.includes(v))

     const isNodePartiallySelected = (node: PermissionNode): boolean => node.allDescendantValues.some(v => selectedValues.includes(v))

     const handleSelect = (node: PermissionNode) => {
          let newSelection = [...selectedValues]
          const isFull = isNodeFullySelected(node)

          if (isFull) {
               // UNCHECK: Remove this node and all descendants
               newSelection = newSelection.filter(v => !node.allDescendantValues.includes(v))
          } else {
               // CHECK: Add this node and all descendants
               newSelection = Array.from(new Set([...newSelection, ...node.allDescendantValues]))
          }

          formikProps.setFieldValue(name, newSelection)
     }

     // --- 4. RECURSIVE RENDER ---
     const renderBranch = (node: PermissionNode, level = 0) => {
          const hasChildren = node.children.length > 0
          const isExpanded = expandedIds.includes(node.value) || !!searchTerm
          const isFull = isNodeFullySelected(node)
          const isPartial = isNodePartiallySelected(node) && !isFull

          return (
               <div key={node.value} className="d-flex flex-column">
                    <div
                         className="d-flex align-items-center py-2 px-3 rounded bg-hover-light cursor-pointer border-bottom border-gray-100"
                         style={{ paddingLeft: `${level * 1.5 + 0.5}rem` }}
                    >
                         {/* Toggle Expand Arrow */}
                         <div
                              className="w-25px h-25px d-flex align-items-center justify-content-center bg-hover-light-primary rounded me-1"
                              onClick={e => {
                                   e.stopPropagation()
                                   setExpandedIds(prev => (prev.includes(node.value) ? prev.filter(id => id !== node.value) : [...prev, node.value]))
                              }}
                         >
                              {hasChildren && <i className={clsx("fa-solid fa-caret-right fs-7 text-gray-400 rotate-transition", isExpanded && "rotate-90")} />}
                         </div>

                         {/* Custom Checkbox logic */}
                         <div className="form-check form-check-custom form-check-solid form-check-sm" onClick={() => handleSelect(node)}>
                              <input className={clsx("form-check-input", isPartial && "form-check-indeterminate")} type="checkbox" checked={isFull || isPartial} readOnly />
                              <label className={clsx("form-check-label fs-7 cursor-pointer ms-2", isFull ? "text-primary fw-bold" : "text-gray-700")}>{node.label}</label>
                         </div>
                    </div>

                    {hasChildren && isExpanded && <div className="ms-5 border-start border-gray-200">{node.children.map(child => renderBranch(child, level + 1))}</div>}
               </div>
          )
     }

     // --- 5. EFFECTS & LABELS ---
     useEffect(() => {
          const handleClickOutside = (e: MouseEvent) => {
               if (dropdownRef.current && !dropdownRef.current.contains(e.target as Node)) setIsOpen(false)
          }
          document.addEventListener("mousedown", handleClickOutside)
          return () => document.removeEventListener("mousedown", handleClickOutside)
     }, [])

     const displayLabel = useMemo(() => {
          if (selectedValues.length === 0) return "Select permissions..."
          return `${selectedValues.length} permission(s) selected`
     }, [selectedValues])

     return (
          <div className={clsx("position-relative w-100", classNameLayout)} ref={dropdownRef}>
               {label && <label className="fs-6 fw-bolder text-gray-900 mb-2 d-block">{label}</label>}

               <div
                    className={clsx("form-control form-control-solid d-flex align-items-center justify-content-between cursor-pointer", {
                         "border-danger": isInvalid,
                    })}
                    onClick={() => setIsOpen(!isOpen)}
               >
                    <span className="text-gray-700 fs-7 fw-bold text-truncate">{displayLabel}</span>
                    <i className={clsx("fa-solid fa-chevron-down fs-9 text-gray-400 transition-all", isOpen && "rotate-180")}></i>
               </div>

               {isOpen && (
                    <div className="card shadow-lg position-absolute w-100 mt-1 border border-gray-200 z-index-1" style={{ zIndex: 1200 }}>
                         <div className="p-4 border-bottom border-gray-100">
                              <div className="input-group input-group-sm input-group-solid">
                                   <span className="input-group-text">
                                        <i className="fa-solid fa-magnifying-glass fs-7" />
                                   </span>
                                   <input
                                        type="text"
                                        className="form-control fs-7"
                                        placeholder="Search..."
                                        autoFocus
                                        value={searchTerm}
                                        onChange={e => setSearchTerm(e.target.value)}
                                   />
                              </div>
                         </div>

                         <div className="card-body p-2" style={{ maxHeight: "400px", overflowY: "auto" }}>
                              {filteredTree.length > 0 ? filteredTree.map(root => renderBranch(root)) : <div className="text-muted fs-7 p-4 text-center">No permissions found</div>}
                         </div>

                         <div className="p-3 bg-light rounded-bottom">
                              <button type="button" className="btn btn-primary btn-sm w-100 fw-bolder" onClick={() => setIsOpen(false)}>
                                   Validate
                              </button>
                         </div>
                    </div>
               )}
          </div>
     )
}

export default PermissionsSelectField
